import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import Card from '../../ui/Card'
import SearchInput from '../../ui/SearchInput'
import Button from '../../ui/Button'
import CenteredState from '../../ui/CenteredState'
import Toast from '../../shared/Toast'
import Loader from '../../shared/Loader'
import ConfiguredAlertsTable from './ConfiguredAlertsTable'
import ConfirmDeleteModal from './ConfirmDeleteModal'
import CreateAlertModal from './CreateAlertModal'
import useConfiguredAlerts from '../hooks/useConfiguredAlerts'
import useAlertMutations from '../hooks/useAlertMutations'

const DEFAULT_PER_PAGE = 10
const SEARCH_DEBOUNCE_MS = 300
const TOAST_CONTAINER_ID = 'configured-alerts-toast'
const SEARCH_KEY = 'q'

const buildHookParams = ({ page, items, search, sort, direction }) => {
  const params = { page, items, sort, direction }
  params[SEARCH_KEY] = search
  return params
}

const ConfiguredAlertsTab = ({ collectId, draftId = null }) => {
  const [page, setPage] = useState(1)
  const [items, setItems] = useState(DEFAULT_PER_PAGE)
  const [searchInput, setSearchInput] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [sort, setSort] = useState(null)
  const [direction, setDirection] = useState('desc')
  const [pendingDelete, setPendingDelete] = useState(null)
  const [isCreateOpen, setIsCreateOpen] = useState(false)

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(searchInput), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(id)
  }, [searchInput])

  useEffect(() => {
    setPage(1)
  }, [debouncedSearch, items, sort, direction])

  const handleSortChange = useCallback((nextSort, nextDirection) => {
    setSort(nextSort)
    setDirection(nextDirection)
  }, [])

  const memoDeps = [page, items, debouncedSearch, sort, direction]
  const hookParams = useMemo(
    () => buildHookParams({ page, items, search: debouncedSearch, sort, direction }), memoDeps
  )

  const { alerts, pagy, loading, hasLoaded, error, refetch } = useConfiguredAlerts(collectId, hookParams)

  const handleMutationSuccess = useCallback(() => {
    refetch()
  }, [refetch])

  const {
    duplicate, destroy, isMutating
  } = useAlertMutations(collectId, {
    onSuccess: handleMutationSuccess,
    toastContainerId: TOAST_CONTAINER_ID
  })

  const handleSearchChange = useCallback(event => setSearchInput(event.target.value), [])

  const handleDuplicate = useCallback(alert => {
    duplicate(alert.id)
  }, [duplicate])

  const handleEdit = useCallback(alert => {
    const url = draftId ? `${alert.edit_url}?draft_id=${draftId}` : alert.edit_url
    window.location.assign(url)
  }, [draftId])

  const handleDelete = useCallback(alert => {
    setPendingDelete(alert)
  }, [])

  const handleCancelDelete = useCallback(() => {
    setPendingDelete(null)
  }, [])

  const handleConfirmDelete = useCallback(async() => {
    if (!pendingDelete)
      return
    const result = await destroy(pendingDelete.id)
    if (result.ok)
      setPendingDelete(null)
  }, [pendingDelete, destroy])

  const isDeletingPending = pendingDelete ? isMutating(pendingDelete.id) : false

  const handleCreateClick = useCallback(() => setIsCreateOpen(true), [])
  const handleCreateClose = useCallback(() => setIsCreateOpen(false), [])

  if (loading && !hasLoaded) {
    return (
      <div>
        <Toast containerId={TOAST_CONTAINER_ID} />
        <CenteredState><Loader /></CenteredState>
      </div>
    )
  }

  return (
    <div>
      <Toast containerId={TOAST_CONTAINER_ID} />
      <div className="tw:flex tw:items-center tw:justify-between tw:gap-3 tw:mb-4">
        <div className="tw:w-full tw:max-w-[531px]">
          <SearchInput
            name="configured-alerts-search"
            placeholder="Rechercher une alerte..."
            value={searchInput}
            onChange={handleSearchChange}
          />
        </div>
        <Button icon="plus" variant="primary" onClick={handleCreateClick}>
          Créer une alerte
        </Button>
      </div>
      <Card title="Alertes configurées" plainHeader>
        <ConfiguredAlertsTable
          alerts={alerts}
          direction={direction}
          error={error}
          isAlertMutating={isMutating}
          loading={loading}
          pagy={pagy}
          sort={sort}
          onDelete={handleDelete}
          onDuplicate={handleDuplicate}
          onEdit={handleEdit}
          onPageChange={setPage}
          onPerPageChange={setItems}
          onSortChange={handleSortChange}
        />
      </Card>
      <ConfirmDeleteModal
        alertName={pendingDelete ? pendingDelete.name : ''}
        isDeleting={isDeletingPending}
        isOpen={pendingDelete !== null}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />
      <CreateAlertModal
        collectId={collectId}
        draftId={draftId}
        isOpen={isCreateOpen}
        toastContainerId={TOAST_CONTAINER_ID}
        onClose={handleCreateClose}
      />
    </div>
  )
}

ConfiguredAlertsTab.propTypes = {
  collectId: PropTypes.number.isRequired,
  draftId: PropTypes.number
}

ConfiguredAlertsTab.defaultProps = {
  draftId: null
}

export default ConfiguredAlertsTab
